import { useToast } from "@chakra-ui/react";
import AwsS3, { AwsS3MultipartOptions } from "@uppy/aws-s3";
import Uppy, { UppyFile } from "@uppy/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import {
  BidV2AwardCriterionResponseDto,
  SupplierBidV2MinimumRequirementResponseDto,
  BidV2QualificationResponseDto,
  UploadDto,
  ViewDocumentAsOwnerDto,
  bffApi,
} from "../../../autogen/bff-api";
import { isApiErrorDto } from "../../errors/isApiErrorDto";
import { uploadWindowClosesAt } from "./uploadWindow";
import { useApiError } from "../../errors/useApiError";
import { isFetchBaseQueryError } from "../../errors/isFetchBaseQueryError";
import { useRumError } from "../../errors/RumComponentContext/useRumError";
import { useAppDispatch } from "../../redux/hooks";
import {
  FileUpload,
  IncompleteFileUploadStatus,
  UploadCompletedStatus,
} from "./FileRow";
import {
  DocumentUploadContext,
  useDocumentsUploader,
} from "./useDocumentsUploader";

class UploadRegistrationFailed extends Error {
  constructor(readonly apiError: unknown) {
    super("Failed to create post request");
  }
}

const serverReason = (error: unknown): string | undefined => {
  const apiError = error instanceof UploadRegistrationFailed ? error.apiError : undefined;
  return apiError && isFetchBaseQueryError(apiError) && isApiErrorDto(apiError.data) && apiError.data.errorCode
    ? apiError.data.errorCode
    : undefined;
};

const isDocumentNotAttachedError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    status?: number;
    data?: { fieldErrors?: Array<{ code?: string }> };
  };
  return (
    e.status === 400 &&
    (e.data?.fieldErrors ?? []).some(
      (fe) => fe.code === "zz_FieldErrorCode_DocumentNotAttached",
    )
  );
};

export const removeQualificationEvidenceDocument = (
  responses: BidV2QualificationResponseDto[],
  qualificationCriterionKey: string,
  documentId: string,
): BidV2QualificationResponseDto[] =>
  responses.map((response) =>
    response.criterionKey === qualificationCriterionKey
      ? {
          ...response,
          evidenceDocumentIds: (response.evidenceDocumentIds ?? []).filter(
            (id) => id !== documentId,
          ),
          evidenceDocuments: (response.evidenceDocuments ?? []).filter(
            (document) => document.id !== documentId,
          ),
        }
      : response,
  );

export const removeAwardCriterionAttachment = (
  responses: BidV2AwardCriterionResponseDto[],
  awardCriterionId: string,
  documentId: string,
): BidV2AwardCriterionResponseDto[] =>
  responses.map((response) =>
    response.criterionId === awardCriterionId
      ? {
          ...response,
          attachmentIds: (response.attachmentIds ?? []).filter(
            (id) => id !== documentId,
          ),
          attachments: (response.attachments ?? []).filter(
            (document) => document.id !== documentId,
          ),
        }
      : response,
  );

export const removeMinimumRequirementAttachment = (
  responses: SupplierBidV2MinimumRequirementResponseDto[],
  minimumRequirementId: string,
  documentId: string,
): SupplierBidV2MinimumRequirementResponseDto[] =>
  responses.map((response) =>
    response.minimumRequirementId === minimumRequirementId
      ? {
          ...response,
          attachmentIds: (response.attachmentIds ?? []).filter((id) => id !== documentId),
          attachments: (response.attachments ?? []).filter((document) => document.id !== documentId),
        }
      : response,
  );

export const useUppyUploader = ({
  entityType,
  entityId,
  eventId,
  reloadEntity,
  qualificationCriterionKey,
  awardCriterionId,
  minimumRequirementId,
}: {
  entityType: DocumentUploadContext;
  entityId: string;
  eventId?: string;
  reloadEntity: () => unknown;
  qualificationCriterionKey?: string;
  awardCriterionId?: string;
  minimumRequirementId?: string;
}) => {
  // Keep the use of the translation hook to avoid unnecessary changes in the uppy memo
  const { t } = useTranslation();
  const [uploads, setUploads] = useState<UploadDto[]>([]);
  const [documents, setDocuments] = useState<ViewDocumentAsOwnerDto[]>([]);
  const documentsRef = useRef<ViewDocumentAsOwnerDto[]>([]);
  documentsRef.current = documents;
  const rumError = useRumError();
  const trigger = useApiError().trigger;
  const [tempFiles, setTempFiles] = useState<
    UppyFile<Record<string, unknown>, Record<string, unknown>>[]
  >([]);
  const toast = useToast();
  const [rejectedUploadIds, setRejectedUploadIds] = useState<string[]>([]);
  const [failedUploadIds, setFailedUploadIds] = useState<string[]>([]);
  const [unregisteredUploadIds, setUnregisteredUploadIds] = useState<string[]>([]);
  const markRejected = useCallback(
    (uploadId: string) =>
      setRejectedUploadIds((ids) => (ids.includes(uploadId) ? ids : [...ids, uploadId])),
    [],
  );
  const { createPostRequest } = useDocumentsUploader({
    entityId,
    eventId,
    entityType,
    refetch: reloadEntity,
    qualificationCriterionKey,
    awardCriterionId,
    minimumRequirementId,
    onUploadRejected: markRejected,
  });
  const dispatch = useAppDispatch();

  const uppy: Uppy = useMemo(() => {
    const instance: Uppy = new Uppy({
      autoProceed: true,
      onBeforeFileAdded(currentFile) {
        currentFile["id"] = uuid();
        return currentFile;
      },
    })
      .use(AwsS3, {
        id: "AwsS3",
        limit: 1,
        async getUploadParameters(
          file: UppyFile<Record<string, unknown>, Record<string, unknown>>,
        ) {
          if (!isFile(file.data)) throw Error("Invalid file");
          const response = await createPostRequest({
            id: file.id,
            name: file.name ?? "",
            file: file.data,
            extension: file.extension,
          });
          if ("data" in response) {
            return {
              url: response.data.url,
              method: "POST",
              fields: {
                "x-amz-date": response.data.renamedXAmzDate,
                "x-amz-signature": response.data.renamedXAmzSignature,
                "x-amz-algorithm": response.data.renamedXAmzAlgorithm,
                "x-amz-credential": response.data.renamedXAmzCredential,
                policy: response.data.policy,
                key: response.data.key,
              },
            };
          } else {
            setUnregisteredUploadIds((ids) => (ids.includes(file.id) ? ids : [...ids, file.id]));
            throw new UploadRegistrationFailed(response.error);
          }
        },
        // The following cast is necessary because @uppy/aws-s3 does not export the correct types
      } as unknown as AwsS3MultipartOptions<
        Record<string, unknown>,
        Record<string, unknown>
      >)
      .on("file-added", (file) => {
        setTempFiles((files) => [...files, file]);
      })
      .on("upload-error", (failedFile, error) => {
        if (failedFile) {
          const failedId = failedFile.id;
          setFailedUploadIds((ids) => (ids.includes(failedId) ? ids : [...ids, failedId]));
          queueMicrotask(() => instance.getFile(failedId) && instance.removeFile(failedId));
        }
        rumError(error, { message: "Document upload failed" });
        const reason = serverReason(error);
        const fileName = failedFile?.name ?? t("The file");
        toast({
          title: t("{{fileName}} could not be uploaded", { fileName }),
          description: reason ? t(reason) : t("Try again."),
          status: "error",
        });
      })
      .on("restriction-failed", (failedFile) => {
        if (failedFile) {
          setTempFiles((files) => files.filter((e) => e.id !== failedFile.id));
        }
        toast({
          title: t("Upload failed!"),
          description: t("Did you try to upload the same file twice?"),
          status: "error",
        });
      });
    return instance;
  }, [createPostRequest, t, toast, rumError]);

  useEffect(() => {
    return () => {
      uppy.destroy();
    };
  }, [uppy]);

  const removeFile = (id: string) => {
    if (uppy.getFile(id)) uppy.removeFile(id);
    setFailedUploadIds((ids) => ids.filter((failedId) => failedId !== id));
    setUnregisteredUploadIds((ids) => ids.filter((unregisteredId) => unregisteredId !== id));
    setTempFiles((files) => files.filter((e) => e.id !== id));
    setUploads((uploads) => uploads.filter((u) => u.id !== id));
  };

  const removeDocument = ({
    documentId,
    uploadId,
  }: {
    documentId: string;
    uploadId: string;
  }) => {
    setDocuments((docs) => docs.filter((d) => d.id !== documentId));
    removeFile(uploadId);
  };

  const removeUpload = async (file: FileUpload) => {
    switch (file.status) {
      case UploadCompletedStatus.UploadCompleted:
        switch (entityType) {
          case "Bid": {
            const response = dispatch(
              bffApi.endpoints.updateBseBid.initiate({
                bidId: entityId,
                editBidRequest: {
                  removeDocument: {
                    value: file.documentId,
                  },
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "BidV2QualificationEvidence": {
            if (!qualificationCriterionKey)
              throw new Error("Missing qualification criterion key");
            const fresh = await dispatch(
              bffApi.endpoints.getBidV2.initiate(
                { bidId: entityId },
                { forceRefetch: true },
              ),
            ).unwrap();
            const response = dispatch(
              bffApi.endpoints.updateBidV2.initiate({
                bidId: entityId,
                bidV2PatchDto: {
                  qualificationResponses: removeQualificationEvidenceDocument(
                    fresh.qualificationResponses ?? [],
                    qualificationCriterionKey,
                    file.documentId,
                  ),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "BidV2AwardCriterionAttachment": {
            if (!awardCriterionId)
              throw new Error("Missing award criterion id");
            const fresh = await dispatch(
              bffApi.endpoints.getBidV2.initiate(
                { bidId: entityId },
                { forceRefetch: true },
              ),
            ).unwrap();
            const response = dispatch(
              bffApi.endpoints.updateBidV2.initiate({
                bidId: entityId,
                bidV2PatchDto: {
                  awardCriterionResponses: removeAwardCriterionAttachment(
                    fresh.awardCriterionResponses ?? [],
                    awardCriterionId,
                    file.documentId,
                  ),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "BidV2MinimumRequirementAttachment": {
            if (!minimumRequirementId)
              throw new Error("Missing minimum requirement id");
            const fresh = await dispatch(
              bffApi.endpoints.getBidV2.initiate(
                { bidId: entityId },
                { forceRefetch: true },
              ),
            ).unwrap();
            const response = dispatch(
              bffApi.endpoints.updateBidV2.initiate({
                bidId: entityId,
                bidV2PatchDto: {
                  minimumRequirementResponses: removeMinimumRequirementAttachment(
                    fresh.minimumRequirementResponses ?? [],
                    minimumRequirementId,
                    file.documentId,
                  ),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          // Rejection attachments are only referenced once the rejection is submitted, so dropping
          // one before that is purely local — there is no entity to patch.
          case "BidV2Rejection":
          case "ParticipationRequestRejection": {
            removeDocument({
              documentId: file.documentId,
              uploadId: file.uploadId,
            });
            toast({
              title: t("Document removed!"),
              description: t("The document was removed successfully"),
              status: "success",
            });
            break;
          }
          case "BidV2": {
            const sendPatch = (docs: ViewDocumentAsOwnerDto[]) => {
              const r = dispatch(
                bffApi.endpoints.updateBidV2.initiate({
                  bidId: entityId,
                  bidV2PatchDto: { documents: docs },
                }),
              );
              r.reset();
              return r;
            };

            let result = await sendPatch(
              documentsRef.current.filter((d) => d.id !== file.documentId),
            );

            if (
              !("data" in result) &&
              isDocumentNotAttachedError(result.error)
            ) {
              const fresh = await dispatch(
                bffApi.endpoints.getBidV2.initiate(
                  { bidId: entityId },
                  { forceRefetch: true },
                ),
              ).unwrap();
              const refreshed = (fresh.documents ?? []).filter(
                (d) => d.id !== file.documentId,
              );
              result = await sendPatch(refreshed);
            }

            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "Contract": {
            if (!file.documentId)
              throw Error("File to be deleted has no document id");
            const response = dispatch(
              bffApi.endpoints.updateContract.initiate({
                contractId: entityId,
                updateContractRequest: {
                  removeDocument: file.documentId,
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else trigger(result.error);
            break;
          }
          case "SourcingEvent": {
            const response = dispatch(
              bffApi.endpoints.updateBasicSourcingEvent.initiate({
                eventId: entityId,
                editSourcingEventRequest: {
                  removeDocument: file.documentId,
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "SourcingEventV2": {
            const sendPatch = (docs: ViewDocumentAsOwnerDto[]) => {
              const r = dispatch(
                bffApi.endpoints.updateSourcingEvent.initiate({
                  eventId: entityId,
                  sourcingEventV2PatchDto: {
                    eventData: { documents: docs },
                  },
                }),
              );
              r.reset();
              return r;
            };

            let result = await sendPatch(
              documentsRef.current.filter((d) => d.id !== file.documentId),
            );

            if (
              !("data" in result) &&
              isDocumentNotAttachedError(result.error)
            ) {
              const fresh = await dispatch(
                bffApi.endpoints.getSourcingEvent.initiate(
                  { eventId: entityId },
                  { forceRefetch: true },
                ),
              ).unwrap();
              const refreshed = (fresh.eventData.documents ?? []).filter(
                (d) => d.id !== file.documentId,
              );
              result = await sendPatch(refreshed);
            }

            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "NegotiationRound": {
            const response = dispatch(
              bffApi.endpoints.editNegotiationRound.initiate({
                negotiationId: entityId,
                editNegotiationRequest: {
                  removeDocument: file.documentId,
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "NegotiationRoundV2": {
            if (!eventId) throw new Error("Missing event id");
            const response = dispatch(
              bffApi.endpoints.patchNegotiationRoundV2.initiate({
                eventId,
                roundId: entityId,
                negotiationRoundV2PatchDto: {
                  documents: documentsRef.current
                    .filter((d) => d.id !== file.documentId)
                    .map((d) => d.id),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "Project": {
            const response = dispatch(
              bffApi.endpoints.updateProject.initiate({
                projectId: entityId,
                projectPatchDto: {
                  documents: documentsRef.current.filter(
                    (d) => d.id !== file.documentId,
                  ),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
          case "Need": {
            const response = dispatch(
              bffApi.endpoints.updateNeed.initiate({
                needId: entityId,
                needPatchDto: {
                  documents: documentsRef.current.filter(
                    (d) => d.id !== file.documentId,
                  ),
                },
              }),
            );
            response.reset();
            const result = await response;
            if ("data" in result) {
              removeDocument({
                documentId: file.documentId,
                uploadId: file.uploadId,
              });
              toast({
                title: t("Document removed!"),
                description: t("The document was removed successfully"),
                status: "success",
              });
            } else {
              trigger(result.error);
            }
            break;
          }
        }
        break;
      case IncompleteFileUploadStatus.UploadFailed: {
        if (!file.registeredOnServer) {
          removeFile(file.uploadId);
          break;
        }
        const response = dispatch(bffApi.endpoints.deleteUpload.initiate({ uploadId: file.uploadId }));
        response.reset();
        const result = await response;
        if ("data" in result) removeFile(file.uploadId);
        else trigger(result.error);
        break;
      }
      case IncompleteFileUploadStatus.UploadingToBrowser:
        removeFile(file.uploadId);
        break;
      case IncompleteFileUploadStatus.UploadingToServer: {
        const response = dispatch(
          bffApi.endpoints.deleteUpload.initiate({
            uploadId: file.uploadId,
          }),
        );
        response.reset();
        const result = await response;
        if ("data" in result) {
          removeFile(file.uploadId);
          toast({
            title: t("Upload canceled!"),
            description: t("The upload was canceled successfully"),
            status: "success",
          });
        } else {
          trigger(result.error);
        }
        break;
      }
    }
  };

  const uploadIdsInDocuments = useMemo(
    () => documents.map((e) => e.fromUploadId),
    [documents],
  );

  const uploadIds = useMemo(
    () =>
      uploads
        .map((e) => e.id)
        .filter((id) => uploadIdsInDocuments.indexOf(id) === -1),
    [uploadIdsInDocuments, uploads],
  );

  const filesUploadingToBrowser: FileUpload[] = useMemo(
    () =>
      tempFiles
        .filter(
          (e) =>
            uploadIds.indexOf(e.id) === -1 &&
            uploadIdsInDocuments.indexOf(e.id) === -1,
        )
        .map((e) => toBrowserSideFileUpload(e, rejectedUploadIds, failedUploadIds, unregisteredUploadIds)),
    [tempFiles, uploadIds, uploadIdsInDocuments, rejectedUploadIds, failedUploadIds, unregisteredUploadIds],
  );

  const filesUploadingToServer: FileUpload[] = useMemo(
    () =>
      uploads
        .filter((e) => uploadIdsInDocuments.indexOf(e.id) === -1)
        .map((e) => toServerSideFileUpload(e, rejectedUploadIds, failedUploadIds, Date.now())),
    [uploadIdsInDocuments, uploads, rejectedUploadIds, failedUploadIds],
  );

  const uploadedFiles: FileUpload[] = useMemo(
    () =>
      documents.map((d) => ({
        status: UploadCompletedStatus.UploadCompleted,
        name: d.name,
        documentId: d.id,
        uploadId: d.fromUploadId,
        uploadedBy: d.uploadedBy,
        uploadedAt: d.uploadedAt,
        size: d.size,
        fileExtension: d.fileExtension,
      })),
    [documents],
  );

  const allFiles: FileUpload[] = useMemo(
    () => [
      ...filesUploadingToBrowser,
      ...filesUploadingToServer,
      ...uploadedFiles,
    ],
    [filesUploadingToBrowser, filesUploadingToServer, uploadedFiles],
  );

  return {
    uppy,
    removeUpload,
    allFiles,
    documents,
    setDocuments,
    setUploads,
    reloadEntity,
  };
};

export const toBrowserSideFileUpload = (
  file: { id: string; name?: string },
  rejectedUploadIds: readonly string[],
  failedUploadIds: readonly string[] = [],
  unregisteredUploadIds: readonly string[] = [],
): FileUpload =>
  rejectedUploadIds.includes(file.id)
    ? {
        status: IncompleteFileUploadStatus.UploadFailed,
        name: file.name ?? "",
        uploadId: file.id,
        rejectedByScan: true,
        registeredOnServer: true,
      }
    : failedUploadIds.includes(file.id)
      ? {
          status: IncompleteFileUploadStatus.UploadFailed,
          name: file.name ?? "",
          uploadId: file.id,
          registeredOnServer: !unregisteredUploadIds.includes(file.id),
        }
      : {
        status: IncompleteFileUploadStatus.UploadingToBrowser,
        name: file.name ?? "",
        uploadId: file.id,
      };

const uploadWindowHasPassed = (upload: UploadDto, now: number): boolean => {
  const closesAt = uploadWindowClosesAt(upload);
  return closesAt !== null && now > closesAt;
};

export const toServerSideFileUpload = (
  upload: UploadDto,
  rejectedUploadIds: readonly string[] = [],
  failedUploadIds: readonly string[] = [],
  now: number = Date.now(),
): FileUpload =>
  upload.scanFailed || rejectedUploadIds.includes(upload.id)
    ? {
        status: IncompleteFileUploadStatus.UploadFailed,
        name: upload.fileName,
        uploadId: upload.id,
        rejectedByScan: true,
        registeredOnServer: true,
      }
    : failedUploadIds.includes(upload.id) || uploadWindowHasPassed(upload, now)
      ? { status: IncompleteFileUploadStatus.UploadFailed, name: upload.fileName, uploadId: upload.id, registeredOnServer: true }
      : {
        status: IncompleteFileUploadStatus.UploadingToServer,
        name: upload.fileName,
        uploadId: upload.id,
      };

const isFile = (file: File | Blob): file is File => {
  return (file as File).lastModified !== undefined;
};
